import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { ExpenseService } from '@/services/expense.service';
import {
  ExpenseStatus,
  ExpenseStatusLabels,
  ExpenseCategoryLabels,
  PaymentMethodLabels,
} from '@/models/expense.model';
import { format } from 'date-fns';
import { Button, Card, Badge, LoadingSpinner, EmptyState, FormTextarea } from '@/components/common';
import { Receipt, ArrowLeft, Edit, Trash2, CheckCircle2, XCircle, DollarSign, Calendar, FileText, CreditCard, MapPin, Car, Briefcase, User } from 'lucide-react';

export const ExpenseDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { profile } = useAuth();
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');

  const { data: expense, isLoading, error } = useQuery({
    queryKey: ['expense', id],
    queryFn: () => (id ? ExpenseService.getExpenseById(id) : null),
    enabled: Boolean(id),
  });

  const approveMutation = useMutation({
    mutationFn: async () => {
      if (!id || !profile) return;
      await ExpenseService.approveExpense(
        id,
        profile.id || '',
        profile.fullName || profile.email || ''
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['expense', id] });
      queryClient.invalidateQueries({ queryKey: ['expenses'] });
    },
  });

  const rejectMutation = useMutation({
    mutationFn: async () => {
      if (!id || !profile) return;
      await ExpenseService.rejectExpense(
        id,
        profile.id || '',
        profile.fullName || profile.email || '',
        rejectionReason
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['expense', id] });
      queryClient.invalidateQueries({ queryKey: ['expenses'] });
      setShowRejectModal(false);
    },
  });

  const markAsPaidMutation = useMutation({
    mutationFn: async () => {
      if (!id || !profile) return;
      await ExpenseService.markExpenseAsPaid(id, profile.id || '');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['expense', id] });
      queryClient.invalidateQueries({ queryKey: ['expenses'] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async () => {
      if (!id) return;
      await ExpenseService.deleteExpense(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['expenses'] });
      navigate('/expenses');
    },
  });

  const getStatusVariant = (status: ExpenseStatus): "success" | "warning" | "danger" | "gray" | "info" => {
    const variants: Record<string, "success" | "warning" | "danger" | "gray" | "info"> = {
      draft: 'gray',
      submitted: 'info',
      pending_approval: 'warning',
      approved: 'success',
      rejected: 'danger',
      pending_payment: 'info',
      paid: 'success',
      cancelled: 'gray',
    };
    return variants[status] || 'gray';
  };

  if (isLoading) {
    return <LoadingSpinner text="Loading expense..." fullScreen />;
  }

  if (error || !expense) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <EmptyState
          icon={Receipt}
          title="Expense not found"
          description="The requested expense does not exist or you may not have access."
          action={
            <Button variant="primary" icon={ArrowLeft} onClick={() => navigate('/expenses')}>
              Back to Expenses
            </Button>
          }
        />
      </div>
    );
  }

  const canEdit = expense.status === ExpenseStatus.draft || expense.status === ExpenseStatus.rejected;
  const canApprove = expense.status === ExpenseStatus.submitted || expense.status === ExpenseStatus.pendingApproval;
  const canMarkAsPaid = expense.status === ExpenseStatus.approved;
  const canDelete = expense.status === ExpenseStatus.draft;

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-6">
        <Button
          variant="ghost"
          icon={ArrowLeft}
          onClick={() => navigate('/expenses')}
        >
          Back to Expenses
        </Button>
      </div>

      {/* Header */}
      <Card className="mb-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-start gap-4 flex-1">
            <div className="w-16 h-16 rounded-lg bg-gradient-to-br from-blue-100 to-blue-200 flex items-center justify-center">
              <Receipt className="w-8 h-8 text-blue-600" />
            </div>
            <div>
              <div className="flex items-center gap-3 mb-2">
                <h1 className="text-2xl font-bold text-gray-900">{expense.description}</h1>
                <Badge variant={getStatusVariant(expense.status)} size="md">
                  {ExpenseStatusLabels[expense.status]}
                </Badge>
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-600 mb-1">
                <span>{ExpenseCategoryLabels[expense.category]}</span>
                <span>•</span>
                <span className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  {format(expense.expenseDate, 'MMMM d, yyyy')}
                </span>
              </div>
              {expense.reportNumber && (
                <Link to={`/expenses/reports/${expense.reportId}`} className="text-sm text-blue-600 hover:underline">
                  Report: {expense.reportNumber}
                </Link>
              )}
            </div>
          </div>
          <div className="text-right">
            <div className="flex items-baseline gap-2">
              <p className="text-4xl font-bold text-gray-900">
                ${expense.amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </p>
              <p className="text-sm text-gray-500">{expense.currency}</p>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="mt-6 pt-6 border-t border-gray-200 flex flex-wrap gap-3">
          {canEdit && (
            <Button
              variant="primary"
              size="sm"
              icon={Edit}
              onClick={() => navigate(`/expenses/${id}/edit`)}
            >
              Edit
            </Button>
          )}
          {canApprove && (
            <>
              <Button
                variant="success"
                size="sm"
                icon={CheckCircle2}
                onClick={() => approveMutation.mutate()}
                isLoading={approveMutation.isPending}
              >
                Approve
              </Button>
              <Button
                variant="danger"
                size="sm"
                icon={XCircle}
                onClick={() => setShowRejectModal(true)}
              >
                Reject
              </Button>
            </>
          )}
          {canMarkAsPaid && (
            <Button
              variant="primary"
              size="sm"
              icon={DollarSign}
              onClick={() => markAsPaidMutation.mutate()}
              isLoading={markAsPaidMutation.isPending}
            >
              Mark as Paid
            </Button>
          )}
          {canDelete && (
            <Button
              variant="danger"
              size="sm"
              icon={Trash2}
              onClick={() => {
                if (window.confirm('Are you sure you want to delete this expense?')) {
                  deleteMutation.mutate();
                }
              }}
              isLoading={deleteMutation.isPending}
            >
              Delete
            </Button>
          )}
        </div>

        {/* Policy Violations */}
        {expense.policyViolations && expense.policyViolations.length > 0 && (
          <Card className="mt-6 bg-yellow-50 border-yellow-200">
            <h3 className="text-sm font-semibold text-yellow-800 mb-3">Policy Violations</h3>
            <ul className="space-y-3">
              {expense.policyViolations.map((violation, idx) => (
                <li key={idx} className="text-sm text-yellow-700 flex items-start gap-3">
                  <div className="flex-shrink-0 w-5 h-5 rounded-full bg-yellow-200 flex items-center justify-center mt-0.5">
                    <span className="text-yellow-800 text-xs">!</span>
                  </div>
                  <div>
                    <strong>{violation.ruleName}:</strong> {violation.message}
                    {violation.suggestedAction && (
                      <p className="text-xs mt-1 text-yellow-600">{violation.suggestedAction}</p>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </Card>
        )}

        {/* Rejection Reason */}
        {expense.status === ExpenseStatus.rejected && expense.rejectionReason && (
          <Card className="mt-6 bg-red-50 border-red-200">
            <h3 className="text-sm font-semibold text-red-800 mb-2">Rejection Reason</h3>
            <p className="text-sm text-red-700">{expense.rejectionReason}</p>
            {expense.rejectedByName && expense.rejectedAt && (
              <p className="text-xs text-red-600 mt-2">
                Rejected by {expense.rejectedByName} on {format(expense.rejectedAt, 'MMMM d, yyyy')}
              </p>
            )}
          </Card>
        )}
      </Card>

      {/* Details Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Employee Information */}
        <Card>
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <User className="w-5 h-5 text-blue-600" />
            Employee Information
          </h2>
          <dl className="space-y-3">
            <div>
              <dt className="text-sm font-medium text-gray-500">Name</dt>
              <dd className="text-sm text-gray-900">{expense.employeeName}</dd>
            </div>
            {expense.employeeEmail && (
              <div>
                <dt className="text-sm font-medium text-gray-500">Email</dt>
                <dd className="text-sm text-gray-900">{expense.employeeEmail}</dd>
              </div>
            )}
            {expense.employeeDepartment && (
              <div>
                <dt className="text-sm font-medium text-gray-500">Department</dt>
                <dd className="text-sm text-gray-900">{expense.employeeDepartment}</dd>
              </div>
            )}
          </dl>
        </Card>

        {/* Payment Information */}
        <Card>
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-blue-600" />
            Payment Information
          </h2>
          <dl className="space-y-3">
            <div>
              <dt className="text-sm font-medium text-gray-500">Payment Method</dt>
              <dd className="text-sm text-gray-900">
                {PaymentMethodLabels[expense.paymentMethod]}
                {expense.cardLastFour && ` (**** ${expense.cardLastFour})`}
              </dd>
            </div>
            {expense.taxAmount && expense.taxAmount > 0 && (
              <div>
                <dt className="text-sm font-medium text-gray-500">Tax Amount</dt>
                <dd className="text-sm text-gray-900">${expense.taxAmount.toFixed(2)}</dd>
              </div>
            )}
            <div>
              <dt className="text-sm font-medium text-gray-500">Tax Deductible</dt>
              <dd className="text-sm text-gray-900">{expense.taxDeductible ? 'Yes' : 'No'}</dd>
            </div>
            {expense.paidAt && (
              <div>
                <dt className="text-sm font-medium text-gray-500">Paid On</dt>
                <dd className="text-sm text-gray-900">{format(expense.paidAt, 'MMMM d, yyyy')}</dd>
              </div>
            )}
            {expense.paymentReference && (
              <div>
                <dt className="text-sm font-medium text-gray-500">Payment Reference</dt>
                <dd className="text-sm text-gray-900">{expense.paymentReference}</dd>
              </div>
            )}
          </dl>
        </Card>

        {/* Merchant Information */}
        {(expense.merchant || expense.location) && (
          <Card>
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <MapPin className="w-5 h-5 text-blue-600" />
              Merchant Information
            </h2>
            <dl className="space-y-3">
              {expense.merchant && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">Merchant</dt>
                  <dd className="text-sm text-gray-900">{expense.merchant}</dd>
                </div>
              )}
              {expense.location && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">Location</dt>
                  <dd className="text-sm text-gray-900">{expense.location}</dd>
                </div>
              )}
              {(expense.city || expense.state) && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">City/State</dt>
                  <dd className="text-sm text-gray-900">
                    {[expense.city, expense.state].filter(Boolean).join(', ')}
                  </dd>
                </div>
              )}
            </dl>
          </Card>
        )}

        {/* Project & Billing */}
        {(expense.projectName || expense.departmentName || expense.billable) && (
          <Card>
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Briefcase className="w-5 h-5 text-blue-600" />
              Project & Billing
            </h2>
            <dl className="space-y-3">
              {expense.projectName && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">Project</dt>
                  <dd className="text-sm text-gray-900">
                    {expense.projectName}
                    {expense.projectCode && ` (${expense.projectCode})`}
                  </dd>
                </div>
              )}
              {expense.departmentName && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">Department</dt>
                  <dd className="text-sm text-gray-900">{expense.departmentName}</dd>
                </div>
              )}
              {expense.costCenterName && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">Cost Center</dt>
                  <dd className="text-sm text-gray-900">{expense.costCenterName}</dd>
                </div>
              )}
              <div>
                <dt className="text-sm font-medium text-gray-500">Billable</dt>
                <dd className="text-sm text-gray-900">
                  {expense.billable ? (
                    <span className="text-green-600">
                      Yes {expense.clientName && `- ${expense.clientName}`}
                    </span>
                  ) : (
                    'No'
                  )}
                </dd>
              </div>
              {expense.billedToClient && expense.invoiceNumber && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">Invoice Number</dt>
                  <dd className="text-sm text-gray-900">{expense.invoiceNumber}</dd>
                </div>
              )}
            </dl>
          </Card>
        )}

        {/* Mileage Details */}
        {expense.mileageDetails && (
          <Card>
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Car className="w-5 h-5 text-blue-600" />
              Mileage Details
            </h2>
            <dl className="space-y-3">
              <div>
                <dt className="text-sm font-medium text-gray-500">From</dt>
                <dd className="text-sm text-gray-900">{expense.mileageDetails.startLocation}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">To</dt>
                <dd className="text-sm text-gray-900">{expense.mileageDetails.endLocation}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Miles</dt>
                <dd className="text-sm text-gray-900">
                  {expense.mileageDetails.miles} {expense.mileageDetails.roundTrip && '(round trip)'}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Rate per Mile</dt>
                <dd className="text-sm text-gray-900">${expense.mileageDetails.ratePerMile.toFixed(2)}</dd>
              </div>
              {expense.mileageDetails.purpose && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">Purpose</dt>
                  <dd className="text-sm text-gray-900">{expense.mileageDetails.purpose}</dd>
                </div>
              )}
            </dl>
          </Card>
        )}
      </div>

      {/* Notes */}
      {expense.notes && (
        <Card className="mt-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <FileText className="w-5 h-5 text-blue-600" />
            Notes
          </h2>
          <p className="text-sm text-gray-700 whitespace-pre-wrap bg-gray-50 p-4 rounded-lg">{expense.notes}</p>
        </Card>
      )}

      {/* Receipts & Attachments */}
      <div className="mt-6 bg-white shadow rounded-lg p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Receipts & Attachments</h2>
        {expense.receiptUrl || (expense.attachments && expense.attachments.length > 0) ? (
          <div className="space-y-3">
            {expense.receiptUrl && (
              <a
                href={expense.receiptUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center p-3 border border-gray-200 rounded-md hover:bg-gray-50"
              >
                <svg className="h-8 w-8 text-gray-400 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <div>
                  <p className="text-sm font-medium text-gray-900">{expense.receiptFileName || 'Receipt'}</p>
                  <p className="text-xs text-gray-500">Click to view</p>
                </div>
              </a>
            )}
            {expense.attachments?.map((attachment, idx) => (
              <a
                key={idx}
                href={attachment.fileUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center p-3 border border-gray-200 rounded-md hover:bg-gray-50"
              >
                <svg className="h-8 w-8 text-gray-400 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <div>
                  <p className="text-sm font-medium text-gray-900">{attachment.fileName}</p>
                  <p className="text-xs text-gray-500">
                    {attachment.fileType} • {(attachment.fileSize / 1024).toFixed(1)} KB
                  </p>
                </div>
              </a>
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-500">No receipts or attachments</p>
        )}
      </div>

      {/* Approval History */}
      {expense.approvalWorkflow && expense.approvalWorkflow.length > 0 && (
        <div className="mt-6 bg-white shadow rounded-lg p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Approval History</h2>
          <div className="flow-root">
            <ul className="-mb-8">
              {expense.approvalWorkflow.map((step, idx) => (
                <li key={idx}>
                  <div className="relative pb-8">
                    {idx !== expense.approvalWorkflow.length - 1 && (
                      <span className="absolute top-4 left-4 -ml-px h-full w-0.5 bg-gray-200" aria-hidden="true" />
                    )}
                    <div className="relative flex space-x-3">
                      <div>
                        <span className={`h-8 w-8 rounded-full flex items-center justify-center ring-8 ring-white ${
                          step.status === 'approved' ? 'bg-green-500' :
                          step.status === 'rejected' ? 'bg-red-500' :
                          step.status === 'pending' ? 'bg-yellow-500' :
                          'bg-gray-400'
                        }`}>
                          {step.status === 'approved' && (
                            <svg className="h-5 w-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          )}
                          {step.status === 'rejected' && (
                            <svg className="h-5 w-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                            </svg>
                          )}
                          {step.status === 'pending' && (
                            <svg className="h-5 w-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                            </svg>
                          )}
                        </span>
                      </div>
                      <div className="min-w-0 flex-1 pt-1.5">
                        <p className="text-sm text-gray-900">
                          <span className="font-medium">{step.approverName || step.approverRole}</span>
                          {' '}{step.status === 'approved' ? 'approved' : step.status === 'rejected' ? 'rejected' : 'pending'}
                        </p>
                        {step.actionDate && (
                          <p className="text-xs text-gray-500">{format(step.actionDate, 'MMMM d, yyyy h:mm a')}</p>
                        )}
                        {step.comments && (
                          <p className="mt-1 text-sm text-gray-600">"{step.comments}"</p>
                        )}
                      </div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* Tags */}
      {expense.tags && expense.tags.length > 0 && (
        <div className="mt-6 bg-white shadow rounded-lg p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Tags</h2>
          <div className="flex flex-wrap gap-2">
            {expense.tags.map((tag) => (
              <span
                key={tag}
                className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
              >
                {tag}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Metadata */}
      <div className="mt-6 bg-white shadow rounded-lg p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">History</h2>
        <dl className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <dt className="text-sm font-medium text-gray-500">Created</dt>
            <dd className="text-sm text-gray-900">{format(expense.createdAt, 'MMMM d, yyyy h:mm a')}</dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-gray-500">Last Updated</dt>
            <dd className="text-sm text-gray-900">{format(expense.updatedAt, 'MMMM d, yyyy h:mm a')}</dd>
          </div>
          {expense.submittedAt && (
            <div>
              <dt className="text-sm font-medium text-gray-500">Submitted</dt>
              <dd className="text-sm text-gray-900">{format(expense.submittedAt, 'MMMM d, yyyy h:mm a')}</dd>
            </div>
          )}
          {expense.approvedAt && (
            <div>
              <dt className="text-sm font-medium text-gray-500">Approved</dt>
              <dd className="text-sm text-gray-900">
                {format(expense.approvedAt, 'MMMM d, yyyy h:mm a')}
                {expense.approvedByName && ` by ${expense.approvedByName}`}
              </dd>
            </div>
          )}
        </dl>
      </div>

      {/* Reject Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setShowRejectModal(false)}></div>
            <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Reject Expense</h3>
              <p className="text-sm text-gray-500 mb-4">
                Please provide a reason for rejecting this expense. The employee will be notified.
              </p>
              <textarea
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                rows={4}
                placeholder="Enter rejection reason..."
                className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-red-500 focus:border-red-500"
              />
              <div className="mt-4 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setShowRejectModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => rejectMutation.mutate()}
                  disabled={!rejectionReason.trim() || rejectMutation.isPending}
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:opacity-50"
                >
                  {rejectMutation.isPending ? 'Rejecting...' : 'Reject Expense'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
